import React, { useState, useEffect } from 'react';
import { Users, Shield, DollarSign, Plus, Trash2, Save, AlertCircle, CheckCircle2, ArrowUp, ArrowDown, UserCog, UserX } from 'lucide-react';
import { apiClient } from '../api/client';

const DepartmentApprovalManagement = ({ departmentId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [department, setDepartment] = useState(null);
  const [manager, setManager] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [managerIsApprover, setManagerIsApprover] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState('');
  const [newThreshold, setNewThreshold] = useState(0);
  const [thresholdCurrency, setThresholdCurrency] = useState('GBP');
  const [changingManager, setChangingManager] = useState(false);

  useEffect(() => {
    if (departmentId) {
      fetchApprovalData();
    }
  }, [departmentId]);

  const fetchApprovalData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch all data in parallel
      const [managerRes, approversRes, usersRes, allUsersRes, hierarchyRes] = await Promise.all([
        apiClient.get(`/department-approvals/${departmentId}/manager`),
        apiClient.get(`/department-approvals/${departmentId}/approvers`),
        apiClient.get(`/department-approvals/available-users/${departmentId}`),
        apiClient.get(`/department-approvals/all-users/${departmentId}`),
        apiClient.get(`/department-approvals/${departmentId}/hierarchy`)
      ]);
      
      const approversData = approversRes.data.approvers || approversRes.data;
      const availableUsersData = usersRes.data.approvers || usersRes.data;
      const allUsersData = allUsersRes.data.users || allUsersRes.data;
      const hierarchyData = hierarchyRes.data.approvalSteps || hierarchyRes.data;
      
      setManager(managerRes.data);
      setDepartment({ DepartmentName: managerRes.data.DepartmentName });
      setApprovers(approversData);
      setAvailableUsers(availableUsersData);
      setAllUsers(allUsersData);
      setHierarchy(hierarchyData);
      
      // Use company default currency from manager response
      if (managerRes.data?.CompanyDefaultCurrency) {
        setThresholdCurrency(managerRes.data.CompanyDefaultCurrency);
      }
      
      // Check if manager is in approvers list (SQL already filters IsActive=1)
      if (managerRes.data?.ManagerUserID) {
        const isApprover = approversData.some(
          a => a.UserID === managerRes.data.ManagerUserID
        );
        setManagerIsApprover(isApprover);
      }
    } catch (err) {
      console.error('Error fetching approval data:', err);
      setError('Failed to load approval settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddApprover = async () => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await apiClient.post(`/department-approvals/${departmentId}/approvers`,
        { 
          userId: selectedUser,
          approvalOrder: approvers.length + 1
        });
      
      setSuccess('Approver added successfully');
      setSelectedUser('');
      await fetchApprovalData();
    } catch (err) {
      console.error('Error adding approver:', err);
      setError(err.response?.data?.error || 'Failed to add approver');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveApprover = async (approverId) => {
    if (!confirm('Are you sure you want to remove this approver?')) return;
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await apiClient.delete(`/department-approvals/${departmentId}/approvers/${approverId}`);
      
      setSuccess('Approver removed successfully');
      await fetchApprovalData();
    } catch (err) {
      console.error('Error removing approver:', err);
      setError(err.response?.data?.error || 'Failed to remove approver');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateThreshold = async (userId, currentThreshold) => {
    const threshold = prompt(
      `Set threshold amount for this user (0 = all invoices, >0 = invoices >= amount)`,
      currentThreshold
    );
    
    if (threshold === null) return;
    
    const thresholdAmount = parseFloat(threshold);
    if (isNaN(thresholdAmount) || thresholdAmount < 0) {
      setError('Please enter a valid threshold amount (0 or greater)');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await apiClient.put(`/department-approvals/${departmentId}/thresholds/${userId}`,
        { 
          thresholdAmount,
          currency: thresholdCurrency
        });
      
      setSuccess('Threshold updated successfully');
      await fetchApprovalData();
    } catch (err) {
      console.error('Error updating threshold:', err);
      setError(err.response?.data?.error || 'Failed to update threshold');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignManager = async (managerUserId) => {
    if (!managerUserId) return;
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await apiClient.put(`/department-approvals/${departmentId}/manager`,
        { managerUserId });
      
      setSuccess('Department manager assigned successfully');
      setChangingManager(false);
      await fetchApprovalData();
    } catch (err) {
      console.error('Error assigning manager:', err);
      setError(err.response?.data?.error || 'Failed to assign manager');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveManager = async () => {
    if (!confirm('Remove this manager from the department? The department will have no manager assigned.')) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.delete(`/department-approvals/${departmentId}/manager`);
      setSuccess('Department manager removed');
      await fetchApprovalData();
    } catch (err) {
      console.error('Error removing manager:', err);
      setError(err.response?.data?.error || 'Failed to remove manager');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleManagerAsApprover = async (checked) => {
    if (!manager?.ManagerUserID) return;
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      if (checked) {
        // Add manager to approvers
        await apiClient.post(`/department-approvals/${departmentId}/manager-as-approver`,
          {});
        setSuccess('Manager added to approvers list');
      } else {
        // Remove manager from approvers
        await apiClient.delete(`/department-approvals/${departmentId}/manager-as-approver`);
        setSuccess('Manager removed from approvers list');
      }
      
      setManagerIsApprover(checked);
      await fetchApprovalData();
    } catch (err) {
      console.error('Error toggling manager as approver:', err);
      setError(err.response?.data?.error || 'Failed to update approver status');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveApprover = async (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === approvers.length - 1)
    ) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const reordered = [...approvers];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];

    // Build new order payload
    const payload = reordered.map((a, i) => ({
      approverId: a.ApproverID,
      approvalOrder: i + 1
    }));

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await apiClient.put(`/department-approvals/${departmentId}/reorder`,
        { approvers: payload });

      setSuccess('Approval order updated');
      await fetchApprovalData();
    } catch (err) {
      console.error('Error reordering approvers:', err);
      setError(err.response?.data?.error || 'Failed to update approval order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-4xl max-h-[85vh] flex flex-col">
      {/* Header - Fixed */}
      <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-500" />
            Approval Hierarchy
          </h2>
          <p className="text-gray-600 mt-1">
            {department?.DepartmentName} - Manage approvers and thresholds
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto flex-1 px-6 py-4">

      {/* Alerts */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Manager Section */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          Department Manager
        </h3>
        
        {manager?.ManagerUserID ? (
          <div className="bg-white rounded p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {manager.FirstName?.[0]}{manager.LastName?.[0]}
                </div>
                <div>
                  <div className="font-medium text-gray-800">
                    {manager.FirstName} {manager.LastName}
                  </div>
                  <div className="text-sm text-gray-600">{manager.Email}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setChangingManager(!changingManager)}
                  disabled={saving}
                  className="btn btn-secondary btn-sm"
                  title="Change department manager"
                >
                  <UserCog className="w-4 h-4" />
                </button>
                <button
                  onClick={handleRemoveManager}
                  disabled={saving}
                  className="btn btn-danger btn-sm"
                  title="Remove manager from department"
                >
                  <UserX className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Change Manager Dropdown */}
            {changingManager && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <div className="text-sm font-medium text-yellow-800 mb-2">Select new department manager:</div>
                <div className="flex gap-2">
                  <select
                    onChange={(e) => {
                      if (e.target.value) handleAssignManager(e.target.value);
                    }}
                    disabled={saving}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select new manager...</option>
                    {allUsers.map(user => (
                      <option key={user.UserID} value={user.UserID}>
                        {user.FirstName} {user.LastName} - {user.Email}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setChangingManager(false)}
                    className="btn btn-secondary btn-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Include in Approvers Checkbox */}
            <div className="flex items-center gap-2 pl-2 pt-2 border-t border-gray-200">
              <input
                type="checkbox"
                id="managerIsApprover"
                checked={managerIsApprover}
                onChange={(e) => handleToggleManagerAsApprover(e.target.checked)}
                disabled={saving}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <label
                htmlFor="managerIsApprover"
                className="text-sm font-medium text-gray-700 cursor-pointer select-none"
              >
                Include manager in approvers list
              </label>
              <span className="text-xs text-gray-500">(When checked, manager will receive invoices for approval)</span>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded p-3">
            <div className="text-gray-600 mb-3">No manager assigned. Select a user to assign as department manager:</div>
            <select
              onChange={(e) => handleAssignManager(e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select manager...</option>
              {allUsers.map(user => (
                <option key={user.UserID} value={user.UserID}>
                  {user.FirstName} {user.LastName} - {user.Email}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Approvers Section */}
      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-gray-600" />
          Department Approvers
        </h3>
        
        {/* Add Approver */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex gap-3">
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={saving}
            >
              <option value="">Select user to add as approver...</option>
              {availableUsers.map(user => (
                <option key={user.UserID} value={user.UserID}>
                  {user.FirstName} {user.LastName} - {user.Email} ({user.Roles})
                </option>
              ))}
            </select>
            <button
              onClick={handleAddApprover}
              disabled={saving || !selectedUser}
              className="btn btn-success"
            >
              <Plus className="w-4 h-4" />
              Add Approver
            </button>
          </div>
        </div>

        {/* Approvers List */}
        <div className="space-y-2">
          {approvers.length === 0 ? (
            <div className="text-gray-600 italic text-center py-4">
              No additional approvers configured
            </div>
          ) : (
            approvers.map((approver, index) => {
              const isManagerEntry = approver.UserID === manager?.ManagerUserID;
              return (
              <div key={approver.ApproverID} className={`flex items-center justify-between border rounded-lg p-3 ${isManagerEntry ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => handleMoveApprover(index, 'up')}
                      disabled={saving || index === 0}
                      className="text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleMoveApprover(index, 'down')}
                      disabled={saving || index === approvers.length - 1}
                      className="text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${isManagerEntry ? 'bg-blue-600' : 'bg-gray-600'}`}>
                    {index + 1}
                  </div>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold ${isManagerEntry ? 'bg-blue-400' : 'bg-gray-400'}`}>
                    {approver.FirstName?.[0]}{approver.LastName?.[0]}
                  </div>
                  <div>
                    <div className="font-medium text-gray-800 flex items-center gap-2">
                      {approver.FirstName} {approver.LastName}
                      {isManagerEntry && (
                        <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Manager</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{approver.Email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Threshold</div>
                    <div className="font-semibold text-gray-800">
                      {approver.ThresholdAmount === 0 ? (
                        <span className="text-blue-600">All Invoices</span>
                      ) : (
                        `${approver.ThresholdAmount} ${approver.Currency}`
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleUpdateThreshold(approver.UserID, approver.ThresholdAmount)}
                    disabled={saving}
                    className="btn btn-primary btn-sm"
                  >
                    <DollarSign className="w-4 h-4" />
                  </button>
                  {!isManagerEntry && (
                  <button
                    onClick={() => handleRemoveApprover(approver.ApproverID)}
                    disabled={saving}
                    className="btn btn-danger btn-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  )}
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* Approval Flow Preview */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-lg mb-3">Approval Flow Example</h3>
        <div className="text-sm text-gray-600 space-y-2">
          <div>
            <strong>Threshold = 0:</strong> Approver receives ALL invoices (including credit notes)
          </div>
          <div>
            <strong>Threshold &gt; 0:</strong> Approver only receives invoices with SubTotal ≥ threshold
          </div>
          <div className="mt-3 pt-3 border-t border-gray-300">
            <strong>Approval Order:</strong> Sequential by threshold (lowest → highest), then Manager
          </div>
        </div>
        
        {hierarchy.length > 0 && (
          <div className="mt-4 bg-white rounded border border-gray-300 p-3">
            <div className="text-sm font-semibold text-gray-700 mb-2">Current Flow:</div>
            <div className="space-y-1">
              {hierarchy.map((h, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">
                    {idx + 1}
                  </div>
                  <span className="font-medium">{h.FirstName} {h.LastName}</span>
                  <span className="text-gray-600">({h.Role})</span>
                  <span className="text-gray-500">→</span>
                  <span className="text-blue-600 font-semibold">
                    {h.ThresholdAmount === 0 ? 'All Invoices' : `≥ ${h.ThresholdAmount} ${h.Currency}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Permission Notes:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Finance Admin:</strong> Can set Manager thresholds and manage all approvers</li>
              <li><strong>Department Manager:</strong> Can manage Approvers and their thresholds (not own threshold)</li>
              <li><strong>Thresholds:</strong> Can be overridden at GL Account level</li>
            </ul>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default DepartmentApprovalManagement;
